/**
 * offlineQueue.js
 *
 * Persists pending API mutations in IndexedDB so they survive page reloads
 * and are replayed in FIFO order when connectivity is restored.
 *
 * Queue entry shape:
 * {
 *   id        : number        (auto-increment key)
 *   method    : 'post' | 'patch' | 'delete'
 *   url       : string        (relative, e.g. '/markups')
 *   data      : object | null
 *   userId    : string        (x-user-id stamped at queue time)
 *   tempId    : string | null (for offline-created items — POST only)
 *   timestamp : number
 *   retries   : number
 * }
 *
 * Usage:
 *   import { enqueue, count, flush } from './offlineQueue';
 */

const DB_NAME    = 'cf_offline';
const DB_VERSION = 1;
const STORE      = 'queue';

let _db = null;

function getDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror   = (e) => reject(e.target.error);
  });
}

/** Add an operation to the queue. Returns the new entry's auto-id. */
export async function enqueue(entry) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add({
      ...entry,
      timestamp: Date.now(),
      retries: 0,
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

/** Retrieve all queued operations in insertion order. */
export async function getAll() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

/** Remove a specific entry by its auto-id. */
export async function remove(id) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

/** Return the number of pending operations. */
export async function count() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

/** Remove every entry (e.g. after a hard reset). */
export async function clear() {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).clear();
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

/**
 * Replay all queued operations in FIFO order.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 *   The raw axios instance (imported from api.js) — interceptors are still
 *   active but won't re-queue because navigator.onLine is true during flush.
 *
 * @param {{ onIdRemap?: (tempId: string, realId: string) => void }} options
 *   onIdRemap is called after a successful POST so callers can replace the
 *   temporary client-side ID with the real server-assigned ID.
 *
 * @returns {{ synced: number, failed: number }}
 */
export async function flush(axiosInstance, { onIdRemap } = {}) {
  const ops = await getAll();
  let synced = 0;
  let failed = 0;

  for (const op of ops) {
    try {
      const headers = {};
      if (op.userId) headers['x-user-id'] = op.userId;

      const res = await axiosInstance({
        method:  op.method,
        url:     op.url,
        data:    op.data,
        headers,
      });

      // For offline-created items, map the temp ID → real server ID.
      if (op.tempId && onIdRemap) {
        // res is already the response body (the response interceptor strips .data)
        const realId = res?.id ?? res?.markup?.id;
        if (realId) onIdRemap(op.tempId, String(realId));
      }

      await remove(op.id);
      synced++;
    } catch (err) {
      console.warn(
        `[OfflineQueue] Sync failed for ${op.method.toUpperCase()} ${op.url}:`,
        err.message
      );
      failed++;
      // Stop replaying to preserve FIFO order — partial sync is worse than none.
      break;
    }
  }

  return { synced, failed };
}
